import { parseContextVariableValue } from "@/src/shared/utils/dataTypeHelpers";
import { useContextStore } from "@/src/stores/useContextStore";

/**
 * Generates a list of updated context variables for an API call based on:
 * - Form data
 * - A list of parameter configurations
 * - A mapper object
 * - `dirtyFields` from your form state (assumed to exist in scope)
 *
 * @param formData - Key-value pairs representing submitted form data
 * @param params - Array of parameter objects, each having a key and options
 * @param mapper - Maps from "apiParamKey" -> "formDataKey" (or null)
 * @param keyToUpdate - Key-value pairs representing updated context variables
 * @returns A new array of updated context variables
 */
export function getContextVariablesToApiCall({
  formData,
  params = [],
  mapper = {},
  keyToUpdate = {},
}: {
  formData: Record<string, any>,
  params?: {
    key: string;
    options: { type: string; required: boolean; isArray: boolean }[];
  }[],
  mapper?: Record<string, string | null>,
  keyToUpdate?: Record<string, any>,
}): any[] {
  const getVariable = useContextStore.getState().getVariable;

  // Gather unique keys from both params and dirtyFields
  const paramKeys = params?.map(({ key }) => key) ?? [];
  const dirtyKeys = Object.keys(keyToUpdate ?? {});
  const allKeys = Array.from(new Set([...paramKeys, ...dirtyKeys]));
  /**
   * Creates a new context variable object with an updated value.
   * Optionally override its `key` to handle the "mappedKey -> originalKey" scenario.
   */
  function createUpdatedVariable(
    contextVarKey: string,
    contextVar: any,
    newValue: any,
    overrideKey?: string,
  ) {
    return {
      ...contextVar,
      value: newValue,
      ...(overrideKey ? { key: overrideKey, id: undefined } : {}),
    };
  }

  return allKeys.reduce((acc: any[], originalKey: string) => {
    const mappedKey = mapper[originalKey];

    // 1. No mappedKey: Just update the context variable matching originalKey.
    if (!mappedKey) {
      const variable = getVariable(originalKey);
      if (variable) {
        const newValue = parseContextVariableValue(formData[originalKey], {
          dataType: variable.dataType,
          key: originalKey,
          isArray: variable.isArray,
        });
        acc.push(createUpdatedVariable(originalKey, variable, newValue));
      }
      return acc;
    }

    // 2. MappedKey exists: Attempt to get the mapped context variable.
    const mappedVar = getVariable(mappedKey);
    if (!mappedVar) {
      // If we can't find the mapped variable, fall back to updating the original variable.
      const variable = getVariable(originalKey);
      if (variable) {
        const newValue = parseContextVariableValue(formData[originalKey], {
          dataType: variable.dataType,
          key: originalKey,
          isArray: variable.isArray,
        });
        acc.push(createUpdatedVariable(originalKey, variable, newValue));
      }
      return acc;
    }

    // 3. Both originalKey and mappedKey may have variables.
    const originalVar = getVariable(originalKey);
    const newValue = parseContextVariableValue(formData[mappedKey], {
      dataType: mappedVar.dataType,
      key: mappedKey,
      isArray: mappedVar.isArray,
    });

    if (originalVar) {
      // If both exist, update both.
      acc.push(createUpdatedVariable(originalKey, originalVar, newValue));
      acc.push(createUpdatedVariable(mappedKey, mappedVar, newValue));
    } else {
      // If only the mappedVar exists, create an entry for it keyed as originalKey.
      acc.push(
        createUpdatedVariable(mappedKey, mappedVar, newValue, originalKey),
      );
    }

    return acc;
  }, []);
}
